import React, { useState, useEffect } from 'react';
import confetti from 'canvas-confetti';

export const TapLetterGame = ({ handleClose, type, onGameComplete }) => {
  const fullAlphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
  const [remainingLetters, setRemainingLetters] = useState([...fullAlphabet]);
  const [targetLetter, setTargetLetter] = useState('');
  const [correctCount, setCorrectCount] = useState(0);
  const [celebrated, setCelebrated] = useState(false);
  const [letterStatus, setLetterStatus] = useState({});
  const [animateLetter, setAnimateLetter] = useState('');

  const correctSound = new Audio('/sounds/correct.mp3');
  const errorSound = new Audio('/sounds/error.mp3');
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);

  // Load volume from local storage
  useEffect(() => {
    const savedVolume = localStorage.getItem('tapGameVolume');
    if (savedVolume !== null) {
      setVolume(parseFloat(savedVolume));
    }
  }, []);

  // Set the first target letter
  useEffect(() => {
    if (remainingLetters.length > 0) {
      selectNextLetter(remainingLetters);
    }
  }, []);

  // Celebrate on full completion
  useEffect(() => {
    if (correctCount === 26 && !celebrated) {
      // Calculate game completion stats
      const stars = 3; // Perfect completion for finishing all letters
      const score = correctCount * 4; // 4 points per correct letter
      
      // Call completion callback before closing
      onGameComplete?.(stars, score);
      
      handleClose?.();
      confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
      setCelebrated(true);
    }
  }, [correctCount, celebrated, onGameComplete]);

  useEffect(() => {
    if (targetLetter && !isMuted) {
      const speakTimeout = setTimeout(() => {
        const utterance = new SpeechSynthesisUtterance(`Tap the letter ${targetLetter}`);
        utterance.rate = 0.8;
        utterance.volume = volume;
        speechSynthesis.speak(utterance);
      }, 600);

      return () => clearTimeout(speakTimeout); // cleanup if letter changes too fast
    }
  }, [targetLetter, isMuted, volume]);

  const selectNextLetter = (letters) => {
    let next;
    if (type === 'sequence') {
      next = letters[0];
    } else {
      next = letters[Math.floor(Math.random() * letters.length)];
    }
    setTargetLetter(next);
  };

  const handleClick = (letter) => {
    if (letter === targetLetter) {
      if (!isMuted) {
        correctSound.volume = volume;
        correctSound.play();
      }

      setAnimateLetter(letter);
      setLetterStatus(prev => ({ ...prev, [letter]: 'correct' }));
      setTimeout(() => setAnimateLetter(null), 500);

      const updatedLetters = remainingLetters.filter(l => l !== letter);
      setRemainingLetters(updatedLetters);
      setCorrectCount(prev => prev + 1);

      if (updatedLetters.length > 0) {
        selectNextLetter(updatedLetters);
      }
    } else {
      if (!isMuted) {
        errorSound.volume = volume;
        errorSound.play();
      }

      setLetterStatus(prev => ({ ...prev, [letter]: 'wrong' }));
      setTimeout(() => {
        setLetterStatus(prev => ({ ...prev, [letter]: null }));
      }, 1000);
    }
  };

  return (
    <div className="tap-letter-board-bg">
      <div className="tap-header-bar mb-1">
        <div className="tap-score-box">
          <span role="img" aria-label="score" style={{ fontSize: 22, marginRight: 6 }}>🏆</span>
          <span className="tap-score-label">Score</span>
          <span className="tap-score-value">{correctCount}/26</span>
        </div>
        <div className="tap-letter-target">
          <span className="tap-letter-label">Tap the letter:</span>
          <span className="tap-letter-value">{targetLetter}</span>
        </div>
        <div className="tap-volume-box">
          <span role="img" aria-label="volume" style={{ fontSize: 20, marginRight: 4 }}>🔊</span>
          <input
            id="volume"
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={volume}
            onChange={(e) => {
              const newVol = parseFloat(e.target.value);
              setVolume(newVol);
              localStorage.setItem('tapGameVolume', newVol);
            }}
            style={{ width: '90px', verticalAlign: 'middle' }}
          />
          <span className="tap-volume-value">{Math.round(volume * 100)}%</span>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary ms-2"
            style={{ borderRadius: 12, padding: '2px 8px', marginLeft: 8 }}
            title="Replay Letter"
            onClick={() => {
              if (targetLetter && !isMuted) {
                const utterance = new window.SpeechSynthesisUtterance(`Tap the letter ${targetLetter}`);
                utterance.rate = 0.8;
                utterance.volume = volume;
                window.speechSynthesis.speak(utterance);
              }
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 20, verticalAlign: 'middle' }}>replay</span>
          </button>
        </div>
      </div>

      <div className="tap-letter-btn-grid">
        {fullAlphabet.map(l => (
          <button
            key={l}
            className={`btn m-1 letter-btn
              ${animateLetter === l ? 'pulse' : ''}
              ${letterStatus[l] === 'correct' ? 'btn-success text-white selected-correct' : ''}
              ${letterStatus[l] === 'wrong' ? 'btn-danger text-white shake' : ''}
              ${!letterStatus[l] ? 'btn-outline-primary' : ''}
            `}
            onClick={() => handleClick(l)}
            disabled={!remainingLetters.includes(l)}
          >
            <img
              src={`/letter/${l}.png`}
              alt={l}
              className="letter-img"
            />
          </button>
        ))}
      </div>

      {correctCount === 26 && <h4 className="text-success mt-3">🎉 Well done! All letters completed.</h4>}

      <style>
        {`
          .tap-letter-board-bg {
            background: url('/image/board.png'), linear-gradient(135deg,rgba(62, 62, 62, 0.3) 0%,rgba(15, 15, 14, 0.41) 100%);
            background-size: cover, cover;
            background-position: center, center;
            border-radius: 32px;
            box-shadow: 0 6px 32px #e0c68a33;
            padding: 32px 12px 24px 12px;
            margin: 0 auto;
            max-width: 900px;
            width: 98%;
            min-height: 400px;
            display: flex;
            flex-direction: column;
            align-items: center;
            position: relative;
            z-index: 2;
            animation: board-pop 0.5s cubic-bezier(.68,-0.55,.27,1.55);
          }
          @keyframes board-pop {
            0% { transform: scale(0.9); opacity: 0; }
            100% { transform: scale(1); opacity: 1; }
          }
          .tap-header-bar {
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: space-between;
            border-radius: 20px;
            box-shadow: 0 2px 8px rgba(224,198,138,0.10);
            padding: 1px 18px 18px 18px;
            margin-bottom: 18px;
            gap: 0;
          }
          .tap-score-box,
          .tap-volume-box {
            display: flex;
            align-items: center;
            border-radius: 14px;
            padding: 6px 14px;
            font-weight: 700;
            font-size: 1.1rem;
            color: #b8860b;
            background: rgba(255, 255, 255, 0.86);
            min-width: 110px;
            box-shadow: 0 1px 4px rgba(224,198,138,0.10);
          }
          .tap-score-label {
            margin-right: 6px;
            font-size: 1rem;
            color: #b8860b;
          }
          .tap-score-value {
            font-size: 1.1rem;
            color: #1976d2;
            margin-left: 4px;
            font-weight: 700;
          }
          .tap-letter-target {
            display: flex;
            align-items: center;
            justify-content: center;
            background: rgba(224,198,138,0.92);
            border-radius: 18px;
            padding: 8px 32px;
            box-shadow: 0 1px 4px rgba(224,198,138,0.10);
            margin: 0 18px;
            min-width: 180px;
          }
          .tap-letter-label {
            font-size: 1.1rem;
            color: #3e2723;
            margin-right: 8px;
            font-weight: 600;
          }
          .tap-letter-value {
            font-size: 2rem;
            color: #1976d2;
            font-weight: 900;
            letter-spacing: 2px;
            text-shadow: 1px 1px 8px #fff, 0 0 8px #e0c68a;
          }
          .tap-volume-value {
            margin-left: 8px;
            font-size: 1rem;
            color: #1976d2;
            font-weight: 700;
            min-width: 36px;
            display: inline-block;
            text-align: right;
          }
          .tap-letter-btn-grid {
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            align-items: center;
            width: 100%;
            min-height: 320px;
            margin-top: 10px;
            gap: 8px;
          }
          .letter-btn {
          padding: 0;
          border-radius: 18px !important;
          background: linear-gradient(135deg,rgb(250, 248, 240) 0%,rgb(248, 228, 166) 100%) !important;
          box-shadow: 0 2px 8px rgba(0,0,0,0.10);
          border: 2px solid rgba(223, 214, 192, 0.65) !important;
          min-width: 90px;
          min-height: 60px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: transform 0.1s, box-shadow 0.2s;
        }
        .letter-btn:active, .letter-btn:focus {
          transform: scale(0.96);
          box-shadow: 0 0 0 3px #ffe08255;
        }
          .letter-img {
            width: 90px;
            height: 60px;
            object-fit: contain;
            padding: 6px;
            background: none;
            transition: filter 0.2s;
          }
          .pulse {
            animation: pulse 0.5s;
          }
          @keyframes pulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.12); }
            100% { transform: scale(1); }
          }
          .shake {
            animation: shake 0.4s;
          }
          @keyframes shake {
            0% { transform: translateX(0); }
            20% { transform: translateX(-8px); }
            40% { transform: translateX(8px); }
            60% { transform: translateX(-8px); }
            80% { transform: translateX(8px); }
            100% { transform: translateX(0); }
          }
          .selected-correct,
          .btn-success.selected-correct {
            // background: linear-gradient(135deg,rgb(196, 208, 90) 0%,rgb(196, 208, 90) 100%) !important;
            background: #000000 !important;
            border-color:rgb(0, 245, 253) !important;
            color: #fff !important;
            box-shadow: 0 0 16px 2px rgba(122, 255, 244, 0.6);
            animation: correct-pop 0.5s;
            transparent: none !important;
          }
          @keyframes correct-pop {
            0% { transform: scale(1.15); }
            100% { transform: scale(1); }
          }
          /* Responsive styles */
          @media (max-width: 1024px) {
            .tap-letter-board-bg {
              max-width: 99vw;
              padding: 18px 2vw 18px 2vw;
              min-height: 260px;
            }
            .tap-letter-btn-grid {
              min-height: 120px;
            }
            .letter-img {
              width: 70px;
              height: 48px;
            }
            .letter-btn {
              min-width: 70px;
              min-height: 48px;
            }
          }
          @media (max-width: 700px) {
            .tap-header-bar {
              flex-direction: column;
              gap: 10px;
              padding-bottom: 10px;
            }
            .tap-letter-target {
              min-width: 0;
              width: 100%;
              padding: 8px 0;
              margin: 8px 0;
            }
            .tap-letter-board-bg {
              padding: 10px 2vw 10px 2vw;
              min-height: 180px;
            }
            .tap-letter-btn-grid {
              min-height: 80px;
              gap: 4px;
            }
            .letter-img {
              width: 48px;
              height: 32px;
            }
            .letter-btn {
              min-width: 48px;
              min-height: 32px;
            }
          }
          @media (max-width: 480px) {
            .tap-letter-board-bg {
              padding: 6px 1vw 6px 1vw;
              min-height: 120px;
            }
            .tap-header-bar {
              padding: 6px 4px 6px 4px;
            }
            .tap-letter-btn-grid {
              gap: 2px;
            }
            .letter-img {
              width: 36px;
              height: 24px;
            }
            .letter-btn {
              min-width: 36px;
              min-height: 24px;
            }
          }
        `}
      </style>
    </div>
  );
};


// import React, { useState, useEffect } from 'react';
// import confetti from 'canvas-confetti';

// export const TapLetterGame = ({ handleClose }) => {
//   const fullAlphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
//   const [remainingLetters, setRemainingLetters] = useState([...fullAlphabet]);
//   const [targetLetter, setTargetLetter] = useState('');
//   const [correctCount, setCorrectCount] = useState(0);
//   const [celebrated, setCelebrated] = useState(false);
//   const [feedback, setFeedback] = useState({ letter: null, result: null }); // letter + result
//   const [letterStatus, setLetterStatus] = useState({});
//   const [animateLetter, setAnimateLetter] = useState('');

//   const correctSound = new Audio('/sounds/correct.mp3');
//   const errorSound = new Audio('/sounds/error.mp3');
//   const [isMuted, setIsMuted] = useState(false);
//   const [volume, setVolume] = useState(1); // range: 0 to 1

//   // useEffect(() => {
//   //   if (remainingLetters.length > 0) {
//   //     const next = remainingLetters[Math.floor(Math.random() * remainingLetters.length)];
//   //     setTargetLetter(next);
//   //   }
//   // }, [remainingLetters]);

//   useEffect(() => {
//     const savedVolume = localStorage.getItem('tapGameVolume');
//     if (savedVolume !== null) {
//       setVolume(parseFloat(savedVolume));
//     }
//   }, []);

//   useEffect(() => {
//     if (remainingLetters.length > 0) {
//       const next = remainingLetters[Math.floor(Math.random() * remainingLetters.length)];
//       setTargetLetter(next);

//       // Play a slight delay to allow for any animation/sound to finish first (e.g., 600ms)
//       // const speakTimeout = setTimeout(() => {
//       //   const utterance = new SpeechSynthesisUtterance(`Tap the letter of ${next}`);
//       //   utterance.rate = 0.8;
//       //   speechSynthesis.speak(utterance);
//       // }, 600); // adjust delay as needed
//       // return () => clearTimeout(speakTimeout); // cleanup on unmount or re-run

//       if (!isMuted) {
//         const speakTimeout = setTimeout(() => {
//           const utterance = new SpeechSynthesisUtterance(`Tap the letter of ${next}`);
//           utterance.rate = 0.8;
//           utterance.volume = volume; // Set speech volume
//           speechSynthesis.speak(utterance);
//         }, 600);
//         return () => clearTimeout(speakTimeout);
//       }

//     }
//   }, [remainingLetters]);


//   useEffect(() => {
//     if (correctCount === 26 && !celebrated) {
//       handleClose()
//       confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
//       setCelebrated(true);
//     }
//   }, [correctCount, celebrated]);

//   const handleClick = (letter) => {
//     if (letter === targetLetter) {
//       // correctSound.play();
//       if (!isMuted) {
//         correctSound.volume = volume;
//         correctSound.play();
//       }
//       setAnimateLetter(letter);
//       setLetterStatus(prev => ({ ...prev, [letter]: 'correct' }));
//       setTimeout(() => setAnimateLetter(null), 500);
//       setCorrectCount(prev => prev + 1);
//       setRemainingLetters(prev => prev.filter(l => l !== letter));
//     } else {
//       // errorSound.play();
//       if (!isMuted) {
//         errorSound.volume = volume;
//         errorSound.play();
//       }
//       setLetterStatus(prev => ({ ...prev, [letter]: 'wrong' }));
//       setTimeout(() => {
//         setLetterStatus(prev => ({ ...prev, [letter]: null }));
//       }, 1000);
//       // alert('❌ Try Again');
//     }
//   };

//   return (
//     <div>
//       <div className="d-flex justify-content-between">
//         <h5>Tap the letter: <b>{targetLetter}</b></h5>
//         <div className="mb-2">
//           <label htmlFor="volume">🔊 Volume: {Math.round(volume * 100)}%</label>
//           <input
//             id="volume"
//             type="range"
//             min="0"
//             max="1"
//             step="0.01"
//             value={volume}
//             onChange={(e) => {
//               const newVol = parseFloat(e.target.value);
//               setVolume(newVol);
//               localStorage.setItem('tapGameVolume', newVol);
//             }}
//             style={{ width: '150px', marginLeft: '10px' }}
//           />
//         </div>
//       </div>
//       <p>Score: {correctCount}/26</p>

//       <div className="d-flex flex-wrap">
//         {fullAlphabet.map(l => {
//           let btnClass = 'btn-outline-primary';
//           let animClass = '';

//           if (feedback.letter === l) {
//             if (feedback.result === 'correct') {
//               btnClass = 'btn-success';
//               animClass = 'pulse';
//             } else if (feedback.result === 'wrong') {
//               btnClass = 'btn-danger';
//               animClass = 'shake';
//             }
//           }

//           return (
//             <button
//               key={l}
//               className={`btn m-1 ${animateLetter === l ? 'pulse' : ''} ${letterStatus[l] === 'correct' ? 'btn-success text-white' :
//                 letterStatus[l] === 'wrong' ? 'btn-danger text-white shake' :
//                   'btn-outline-primary'
//                 }`}
//               onClick={() => handleClick(l)}
//               disabled={!remainingLetters.includes(l)}
//               style={{ padding: 0 }}
//             >
//               <img
//                 src={`/letter/${l}.png`}
//                 alt={l}
//                 style={{ width: '100px', height: '70px', objectFit: 'contain', padding: '6px' }}
//               />
//             </button>

//           );
//         })}
//       </div>
//       {correctCount === 26 && <h4 className="text-success mt-3">🎉 Well done! All letters completed.</h4>}
//     </div>
//   );
// };
